// src/components/HeadingTooltip.jsx
//
// HeadingTooltip — portal-based column-heading popup.
//
// Renders its children via ReactDOM.createPortal into document.body so the
// popup escapes any overflow:hidden/auto ancestor (table wrappers, sticky-
// header contexts, card panels). Position is computed from the triggerRef's
// bounding rect (4 px below the trigger's bottom edge, left-aligned to the
// trigger's left edge) and is recomputed on every scroll event while open,
// so the popup follows its anchor as the page scrolls.
//
// After each render the popup measures its own rendered width and the viewport
// width. If the right edge of the popup would exceed the viewport the left
// position is clamped so the popup stays fully on-screen.
//
// Usage:
//   const ref = useRef(null);
//   <th ref={ref} onMouseEnter={() => setOpen(true)} ...>
//   {open && (
//     <HeadingTooltip
//       triggerRef={ref}
//       role="tooltip"
//       data-testid="th-tooltip-mykey"
//       style={TH_TIP_STYLE}
//     >
//       <ColorLegendPopup rows={…} />
//     </HeadingTooltip>
//   )}
//
// Props spread onto the portal <span>:
//   triggerRef — React ref attached to the trigger element
//   children   — popup content
//   style      — container style (background, border, radius, padding, …)
//   …rest      — any other props (role, data-testid, etc.)

import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import ReactDOM from "react-dom";

export function HeadingTooltip({ triggerRef, children, style, ...rest }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);

  useEffect(() => {
    function recompute() {
      if (!triggerRef?.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    recompute();
    window.addEventListener("scroll", recompute, true);
    return () => window.removeEventListener("scroll", recompute, true);
  }, [triggerRef]);

  // After each position change, measure the rendered popup dimensions and
  // clamp left/top so the popup does not overflow the right or bottom edges
  // of the viewport.
  useLayoutEffect(() => {
    if (!popupRef.current) return;
    const popupRect = popupRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    setPos(prev => {
      let { top, left } = prev;

      if (popupRect.right > viewportWidth) {
        left = Math.max(0, left - (popupRect.right - viewportWidth));
      }

      const popupHeight = popupRect.height;
      if (top + popupHeight > viewportHeight) {
        const triggerRect = triggerRef?.current?.getBoundingClientRect();
        const triggerTop = triggerRect ? triggerRect.top : top;
        top = Math.max(0, triggerTop - popupHeight - 4);
      }

      if (top === prev.top && left === prev.left) return prev;
      return { top, left };
    });
  }, [pos.left, pos.top, triggerRef]);

  return ReactDOM.createPortal(
    <span
      ref={popupRef}
      aria-live="polite"
      {...rest}
      style={{
        ...style,
        position: "fixed",
        top:      pos.top,
        left:     pos.left,
      }}
    >
      {children}
    </span>,
    document.body,
  );
}
